// @flow

import * as React from 'react';
import { Box } from 'react-system';
import { Filter } from '../../../controls/Filter';
import { useLocale } from '../../../hooks/locale';
import { useUserData } from '../../../hooks/user-data';
import { createDescriptor } from '../../../controls/router';
import type { AggregatesListingsFilters_root } from '../__generated__/AggregatesListingsFilters_root.graphql.js';
import { NumberMonospace } from './NumberMonospace';
import {
  offerTypeDescriptor,
  salePricePerSurfaceDescriptor,
  rentPricePerAdditionalDescriptor,
  pageDescriptor,
  priceFilterDescriptor,
} from './sharedDescriptors';
import { FilterMinMax } from './FilterMinMax';

const currencyFormatOptions = (currency: string) => ({
  style: 'currency',
  currency,
  currencyDisplay: 'symbol',
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
});

const combinedDescriptor = createDescriptor(
  priceFilterDescriptor,
  offerTypeDescriptor,
  salePricePerSurfaceDescriptor,
  rentPricePerAdditionalDescriptor,
  pageDescriptor,
);

type Props = {|
  root: AggregatesListingsFilters_root,
  inline?: boolean,
  shouldFlush: boolean,
|};

const makeSuggestionsConstraintsRangesSalePrice = (countryCode, inline) => {
  switch (countryCode) {
    case 'CH': {
      return inline === true
        ? {
            min: [
              { from: 0, to: 1_000_000, step: 50_000 },
              { from: 1_000_000, to: 2_500_000, step: 100_000 },
              { from: 2_500_000, to: Infinity, step: 250_000 },
            ],
            max: [
              { from: 50_000, to: 1_000_000, step: 50_000 },
              { from: 1_000_000, to: 2_500_000, step: 100_000 },
              { from: 2_500_000, to: Infinity, step: 250_000 },
            ],
          }
        : {
            min: [
              { from: 0, to: 1_000_000, step: 100_000 },
              { from: 1000000, to: 2500000, step: 250_000 },
              { from: 2500000, to: Infinity, step: 500_000 },
            ],
            max: [
              { from: 600_000, to: 1_000_000, step: 100_000 },
              { from: 1_000_000, to: 2_500_000, step: 250_000 },
              { from: 2_500_000, to: Infinity, step: 500_000 },
            ],
          };
    }
    case 'FR': {
      return {
        min: [
          { from: 0, to: 300_000, step: 50_000 },
          { from: 300_000, to: 500_000, step: 100_000 },
          { from: 500_000, to: 1_100_000, step: 200_000 },
          { from: 1_000_000, to: Infinity, step: 500_000 },
        ],
        max: [
          { from: 100_000, to: 300_000, step: 50_000 },
          { from: 300_000, to: 400_000, step: 100_000 },
          { from: 400_000, to: 1000_000, step: 200_000 },
          { from: 1_000_000, to: Infinity, step: 500_000 },
        ],
      };
    }
    default:
      return {
        min: [{ from: 0, to: Infinity, step: 100_000 }],
        max: [{ from: 100_000, to: Infinity, step: 100_000 }],
      };
  }
};

const makeSuggestionsConstraintsRangesRentPrice = countryCode => {
  switch (countryCode) {
    case 'CH': {
      return {
        min: [
          { from: 0, to: 600, step: 600 },
          { from: 600, to: Infinity, step: 200 },
        ],
        max: [{ from: 1200, to: Infinity, step: 200 }],
      };
    }
    case 'FR': {
      return {
        min: [
          { from: 200, to: 500, step: 100 },
          { from: 500, to: 1100, step: 200 },
          { from: 1000, to: Infinity, step: 200 },
        ],
        max: [
          { from: 300, to: 500, step: 100 },
          { from: 500, to: 1100, step: 200 },
          { from: 1300, to: Infinity, step: 400 },
        ],
      };
    }
    default:
      return {
        min: [{ from: 0, to: Infinity, step: 100_000 }],
        max: [{ from: 100_000, to: Infinity, step: 100_000 }],
      };
  }
};

const makeSuggestionsConstraintsRangesSalePricePerLivingSurface = () => ({
  min: [
    { from: 1000, to: 5000, step: 250 },
    { from: 5000, to: 10_000, step: 500 },
    { from: 10_000, to: Infinity, step: 1000 },
  ],
  max: [
    { from: 1500, to: 5000, step: 250 },
    { from: 5000, to: 10_000, step: 1000 },
    { from: 15_000, to: Infinity, step: 1500 },
  ],
});

const makeSuggestionsConstraintsRangesSalePricePerLandSurface = () => ({
  min: [
    { from: 100, to: 1000, step: 100 },
    { from: 1000, to: 2500, step: 200 },
    { from: 2000, to: Infinity, step: 400 },
  ],
  max: [
    { from: 100, to: 1000, step: 100 },
    { from: 1500, to: 4000, step: 200 },
    { from: 4000, to: Infinity, step: 500 },
  ],
});

const makeSuggestionsConstraintsRangesGrossRentM2yearly = () => ({
  min: [
    { from: 50, to: 350, step: 50 },
    { from: 350, to: 650, step: 100 },
    { from: 850, to: Infinity, step: 200 },
  ],
  max: [
    { from: 50, to: 350, step: 50 },
    { from: 400, to: 800, step: 100 },
    { from: 1000, to: Infinity, step: 200 },
  ],
});

const makeSuggestionsConstraintsRangesGrossRentRoomMonthly = () => ({
  min: [
    { from: 200, to: 800, step: 100 },
    { from: 900, to: 1200, step: 150 },
    { from: 1350, to: Infinity, step: 200 },
  ],
  max: [
    { from: 300, to: 800, step: 100 },
    { from: 1000, to: 1300, step: 150 },
    { from: 1700, to: Infinity, step: 300 },
  ],
});

export const PriceFilter = ({
  root,
  inline,
  shouldFlush,
}: Props): React.Node => {
  const empty = priceFilterDescriptor.useIsEmpty();

  const { t, numberLocale, currency } = useLocale();
  const { countryCode } = useUserData();

  const [open, setOpen] = React.useState(false);
  const [priceFilters, setPriceFilters, flushPriceFilters] =
    combinedDescriptor.useState();

  const flush = () => {
    setPriceFilters({ page: 0 });
    if (shouldFlush) {
      flushPriceFilters();
    }
  };

  const formatOptions = currencyFormatOptions(currency);
  const { offerType } = priceFilters;

  const salePriceSlider = (
    <FilterMinMax
      autoFocus={true}
      title={t('priceFilterTitle')}
      desktopDropdownVariant="inline"
      value={{
        min: priceFilters.salePrice_gte,
        max: priceFilters.salePrice_lte,
      }}
      onChange={({ min, max }) =>
        setPriceFilters({
          salePrice_gte: min,
          salePrice_lte: max,
        })
      }
      onClear={() => {
        setPriceFilters({
          salePrice_gte: null,
          salePrice_lte: null,
        });
        flush();
      }}
      onFlush={flush}
      onDone={() => setOpen(false)}
      suggestionsOptions={{
        format: suggest =>
          suggest >= 1_000_000
            ? numberLocale.formatShort(suggest, formatOptions)
            : numberLocale.format(suggest, formatOptions),
        constraints: {
          minValue: root.aggregatesListingInfo.minSalePrice,
          maxValue: root.aggregatesListingInfo.maxSalePrice,
          ranges: makeSuggestionsConstraintsRangesSalePrice(
            countryCode,
            inline,
          ),
        },
        anyTitle: t('anyPrice'),
      }}
    />
  );

  const rentPriceSlider = (
    <FilterMinMax
      autoFocus={true}
      desktopDropdownVariant="inline"
      title={t('priceRentFilterTitle')}
      value={{
        min: priceFilters.grossRentMonthly_gte,
        max: priceFilters.grossRentMonthly_lte,
      }}
      onChange={({ min, max }) =>
        setPriceFilters({
          grossRentMonthly_gte: min,
          grossRentMonthly_lte: max,
        })
      }
      onClear={() => {
        setPriceFilters({
          grossRentMonthly_gte: null,
          grossRentMonthly_lte: null,
        });
        flush();
      }}
      onFlush={flush}
      onDone={() => setOpen(false)}
      suggestionsOptions={{
        format: suggest =>
          suggest >= 1_000_000
            ? numberLocale.formatShort(suggest, formatOptions)
            : numberLocale.format(suggest, formatOptions),
        constraints: {
          minValue: root.aggregatesListingInfo.minGrossRentMonthly,
          maxValue: root.aggregatesListingInfo.maxGrossRentMonthly,
          ranges: makeSuggestionsConstraintsRangesRentPrice(countryCode),
        },
        anyTitle: t('anyPrice'),
      }}
    />
  );

  const dialogTitle =
    offerType === 'buy' ? t('priceFilterTitle') : t('priceRentFilterTitle');

  const getLabel = (from, to) => {
    if (from != null && to == null) {
      return t('priceOver', {
        price: numberLocale.formatShort(from, formatOptions),
      });
    } else if (from == null && to != null) {
      return t('priceUnder', {
        price: numberLocale.formatShort(to, formatOptions),
      });
    } else if (from != null && to != null) {
      return `${numberLocale.formatShort(
        from,
        formatOptions,
      )} - ${numberLocale.formatShort(to, formatOptions)}`;
    }

    return dialogTitle;
  };

  const label =
    offerType === 'buy'
      ? getLabel(priceFilters.salePrice_gte, priceFilters.salePrice_lte)
      : getLabel(
          priceFilters.grossRentMonthly_gte,
          priceFilters.grossRentMonthly_lte,
        );

  const priceSlider = offerType === 'buy' ? salePriceSlider : rentPriceSlider;

  return inline === true ? (
    priceSlider
  ) : (
    <Filter
      label={<NumberMonospace>{label}</NumberMonospace>}
      // dialogTitle={dialogTitle}
      empty={empty}
      open={open}
      onOpenChange={open => setOpen(open)}
      onReset={() => {
        setPriceFilters(priceFilterDescriptor.defaults);
        flush();
      }}
    >
      <Box
        width={['320px']}
        css={{
          maxHeight: 'calc(100vh - 260px)', // 260 = top bar + some free space
        }}
        p={3}
      >
        {priceSlider}
      </Box>
    </Filter>
  );
};

export const PricePerLivingSurface = ({
  inline,
  shouldFlush,
  root,
}: Props): React.Node => {
  const { t, numberLocale, currency } = useLocale();
  const formatOptions = currencyFormatOptions(currency);

  const [priceFilters, setPriceFilters, flushPriceFilters] =
    combinedDescriptor.useState();

  const flush = () => {
    setPriceFilters({ page: 0 });
    if (shouldFlush) {
      flushPriceFilters();
    }
  };

  return (
    <FilterMinMax
      title={inline === true ? null : t('salePricePerLivingSurfaceAuto')}
      value={{
        min: priceFilters.salePricePerLivingSurface_gte,
        max: priceFilters.salePricePerLivingSurface_lte,
      }}
      onChange={({ min, max }) =>
        setPriceFilters({
          salePricePerLivingSurface_gte: min,
          salePricePerLivingSurface_lte: max,
        })
      }
      onClear={() => {
        setPriceFilters({
          salePricePerLivingSurface_gte: null,
          salePricePerLivingSurface_lte: null,
        });
        flush();
      }}
      onFlush={flush}
      suggestionsOptions={{
        format: suggest => numberLocale.format(suggest, formatOptions),
        constraints: {
          minValue: root.aggregatesListingInfo.minSalePricePerLivingSurface,
          maxValue: root.aggregatesListingInfo.maxSalePricePerLivingSurface,
          ranges: makeSuggestionsConstraintsRangesSalePricePerLivingSurface(),
        },
      }}
    />
  );
};

export const PricePerLandSurface = ({
  inline,
  shouldFlush,
  root,
}: Props): React.Node => {
  const { t, numberLocale, currency } = useLocale();
  const formatOptions = currencyFormatOptions(currency);

  const [priceFilters, setPriceFilters, flushPriceFilters] =
    combinedDescriptor.useState();

  const flush = () => {
    setPriceFilters({ page: 0 });
    if (shouldFlush) {
      flushPriceFilters();
    }
  };

  return (
    <FilterMinMax
      title={inline === true ? null : t('salePricePerLandSurface')}
      value={{
        min: priceFilters.salePricePerLandSurface_gte,
        max: priceFilters.salePricePerLandSurface_lte,
      }}
      onChange={({ min, max }) =>
        setPriceFilters({
          salePricePerLandSurface_gte: min,
          salePricePerLandSurface_lte: max,
        })
      }
      onClear={() => {
        setPriceFilters({
          salePricePerLandSurface_gte: null,
          salePricePerLandSurface_lte: null,
        });
        flush();
      }}
      onFlush={flush}
      suggestionsOptions={{
        format: suggest => numberLocale.format(suggest, formatOptions),
        constraints: {
          minValue: root.aggregatesListingInfo.minSalePricePerLandSurface,
          maxValue: root.aggregatesListingInfo.maxSalePricePerLandSurface,
          ranges: makeSuggestionsConstraintsRangesSalePricePerLandSurface(),
        },
      }}
    />
  );
};

export const RentPricePerM2Year = ({ inline, root }: Props): React.Node => {
  const { t, numberLocale, currency } = useLocale();
  const formatOptions = currencyFormatOptions(currency);

  const [priceFilters, setPriceFilters, flushPriceFilters] =
    combinedDescriptor.useState();

  const flush = () => {
    setPriceFilters({ page: 0 });
    flushPriceFilters();
  };

  return (
    <FilterMinMax
      title={inline === true ? null : t('grossRentM2yearly')}
      value={{
        min: priceFilters.grossRentM2yearly_gte,
        max: priceFilters.grossRentM2yearly_lte,
      }}
      onChange={({ min, max }) =>
        setPriceFilters({
          grossRentM2yearly_gte: min,
          grossRentM2yearly_lte: max,
        })
      }
      onClear={() => {
        setPriceFilters({
          grossRentM2yearly_gte: null,
          grossRentM2yearly_lte: null,
        });
        flush();
      }}
      onFlush={flush}
      suggestionsOptions={{
        format: suggest => numberLocale.format(suggest, formatOptions),
        constraints: {
          minValue: root.aggregatesListingInfo.minGrossRentM2yearly,
          maxValue: root.aggregatesListingInfo.maxGrossRentM2yearly,
          ranges: makeSuggestionsConstraintsRangesGrossRentM2yearly(),
        },
      }}
    />
  );
};

export const RentPricePerRoom = ({
  inline,
  shouldFlush,
  root,
}: Props): React.Node => {
  const { t, numberLocale, currency } = useLocale();
  const formatOptions = currencyFormatOptions(currency);

  const [priceFilters, setPriceFilters, flushPriceFilters] =
    combinedDescriptor.useState();

  const flush = () => {
    setPriceFilters({ page: 0 });
    if (shouldFlush) {
      flushPriceFilters();
    }
  };

  return (
    <FilterMinMax
      title={inline === true ? null : t('grossRentRoomMonthly')}
      value={{
        min: priceFilters.grossRentRoomMonthly_gte,
        max: priceFilters.grossRentRoomMonthly_lte,
      }}
      onChange={({ min, max }) =>
        setPriceFilters({
          grossRentRoomMonthly_gte: min,
          grossRentRoomMonthly_lte: max,
        })
      }
      onClear={() => {
        setPriceFilters({
          grossRentRoomMonthly_gte: null,
          grossRentRoomMonthly_lte: null,
        });
        flush();
      }}
      onFlush={flush}
      suggestionsOptions={{
        format: suggest => numberLocale.format(suggest, formatOptions),
        constraints: {
          minValue: root.aggregatesListingInfo.minGrossRentRoomMonthly,
          maxValue: root.aggregatesListingInfo.maxGrossRentRoomMonthly,
          ranges: makeSuggestionsConstraintsRangesGrossRentRoomMonthly(),
        },
      }}
    />
  );
};
